import type {
  ISecapRuntimeCatalog,
  ISecapRuntimeIndicator,
  ISecapTemplateCoverage,
  ISecapTemplateCoverageField,
  ISecapVersion,
  ISecapVersionTemplateConfiguration,
  SecapSourceStrategy,
  SecapTemplateCoverageStatus,
  SecapTemplateInsertionPolicy,
} from '../../interfaces/secap/ISecapApi';
import type {
  ISecapFieldsSummary,
  ISecapFieldsViewModel,
  ISecapIndicatorCoverageCounts,
  ISecapIndicatorRowViewModel,
  ISecapIndicatorSectionViewModel,
  SecapIndicatorCoverageViewStatus,
} from '../../interfaces/secap/ISecapFields';

const EMPTY_COVERAGE_COUNTS: ISecapIndicatorCoverageCounts = {
  total: 0,
  coveredByService: 0,
  coveredByExtension: 0,
  coveredByEntitySource: 0,
  entityResponsibility: 0,
};

const COVERAGE_LABELS: Readonly<Record<SecapTemplateCoverageStatus, string>> = {
  covered_by_service: 'Coperto',
  covered_by_extension: 'Con estensione',
  covered_by_entity_source: 'Fonte ente',
  entity_responsibility: "A cura dell'ente",
};

const SOURCE_STRATEGY_LABELS: Readonly<Record<SecapSourceStrategy, string>> = {
  riuso_pubblico: 'Fonte pubblica',
  ibrido: 'Servizio + fonte ente',
  sviluppo_interno: 'Servizio SECAP',
  composizione: 'Composizione',
  dato_ente: 'Dato ente',
};

const RUNTIME_STATE_LABELS: Readonly<Record<string, string>> = {
  enabled: 'Attivo',
  not_runtime_ready: 'Non pronto',
  extension_not_active: 'Estensione non attiva',
  not_enabled: 'Non attivo',
  entity_source_missing: 'Fonte ente mancante',
};

const uniqueCoverageFields = (
  fields: readonly ISecapTemplateCoverageField[],
): readonly ISecapTemplateCoverageField[] => {
  const byUuid = new Map<string, ISecapTemplateCoverageField>();
  for (const field of fields) {
    byUuid.set(field.field_uuid, field);
  }
  return [...byUuid.values()];
};

const countCoverageFields = (
  fields: readonly ISecapTemplateCoverageField[],
): ISecapIndicatorCoverageCounts => {
  const uniqueFields = uniqueCoverageFields(fields);
  return uniqueFields.reduce<ISecapIndicatorCoverageCounts>(
    (counts, field) => ({
      total: counts.total + 1,
      coveredByService:
        counts.coveredByService + (field.status === 'covered_by_service' ? 1 : 0),
      coveredByExtension:
        counts.coveredByExtension +
        (field.status === 'covered_by_extension' ? 1 : 0),
      coveredByEntitySource:
        counts.coveredByEntitySource +
        (field.status === 'covered_by_entity_source' ? 1 : 0),
      entityResponsibility:
        counts.entityResponsibility +
        (field.status === 'entity_responsibility' ? 1 : 0),
    }),
    EMPTY_COVERAGE_COUNTS,
  );
};

const coverageStatusForIndicator = (
  fields: readonly ISecapTemplateCoverageField[],
): SecapIndicatorCoverageViewStatus => {
  const statuses = new Set(fields.map((field) => field.status));
  if (statuses.size === 0) {
    return 'not_mapped';
  }
  if (statuses.size > 1) {
    return 'mixed';
  }
  return [...statuses][0];
};

const coverageLabel = (status: SecapIndicatorCoverageViewStatus): string => {
  if (status === 'mixed') {
    return 'Copertura mista';
  }
  if (status === 'not_mapped') {
    return 'Non mappato';
  }
  return COVERAGE_LABELS[status];
};

const scopeLabel = (scope: ISecapRuntimeIndicator['scope']): string | null => {
  if (scope === 'nucleo') {
    return 'Nucleo';
  }
  if (scope === 'estensione') {
    return 'Estensione';
  }
  return null;
};

const runtimeStateLabel = (state: string): string =>
  RUNTIME_STATE_LABELS[state] ?? state.replaceAll('_', ' ');

const sourceLabel = (indicator: ISecapRuntimeIndicator): string => {
  if (indicator.entity_source?.source_name) {
    return indicator.entity_source.source_name;
  }
  if (indicator.public_source_code) {
    return indicator.public_source_code;
  }
  if (indicator.source_strategy) {
    return SOURCE_STRATEGY_LABELS[indicator.source_strategy];
  }
  if (indicator.origin === 'provided_by_entity') {
    return 'Dato ente';
  }
  return 'Servizio SECAP';
};

const fieldsByIndicatorCode = (
  coverage: ISecapTemplateCoverage | null,
  version: ISecapVersion | null,
): ReadonlyMap<string, readonly ISecapTemplateCoverageField[]> => {
  const result = new Map<string, ISecapTemplateCoverageField[]>();
  const fieldsByUuid = new Map<string, ISecapTemplateCoverageField>();

  const append = (indicatorCode: string, field: ISecapTemplateCoverageField): void => {
    const current = result.get(indicatorCode) ?? [];
    if (!current.some((item) => item.field_uuid === field.field_uuid)) {
      current.push(field);
      result.set(indicatorCode, current);
    }
  };

  for (const section of coverage?.sections ?? []) {
    for (const field of section.fields) {
      fieldsByUuid.set(field.field_uuid, field);
      for (const indicatorCode of field.indicator_codes) {
        append(indicatorCode, field);
      }
    }
  }

  for (const [indicatorCode, config] of Object.entries(
    version?.template_configuration.indicators ?? {},
  )) {
    for (const fieldUuid of config.template_field_uuids) {
      const field = fieldsByUuid.get(fieldUuid);
      if (field) {
        append(indicatorCode, field);
      }
    }
  }

  return result;
};

const selectedVersionPolicyByField = (
  version: ISecapVersion | null,
): ReadonlyMap<string, SecapTemplateInsertionPolicy> => {
  const result = new Map<string, SecapTemplateInsertionPolicy>();
  for (const config of Object.values(version?.template_configuration.indicators ?? {})) {
    for (const fieldUuid of config.template_field_uuids) {
      result.set(fieldUuid, config.insertion_policy);
    }
  }
  return result;
};

const automaticInsertionCount = (
  coverage: ISecapTemplateCoverage | null,
  version: ISecapVersion | null,
): number => {
  const versionPolicies = selectedVersionPolicyByField(version);
  return (coverage?.sections ?? [])
    .flatMap((section) => section.fields)
    .filter(
      (field) =>
        (versionPolicies.get(field.field_uuid) ?? field.insertion_policy) === 'automatic',
    ).length;
};

const buildSummary = (
  coverage: ISecapTemplateCoverage | null,
  version: ISecapVersion | null,
): ISecapFieldsSummary => ({
  total: coverage?.counts.total ?? 0,
  coveredByService: coverage?.counts.covered_by_service ?? 0,
  coveredByExtension: coverage?.counts.covered_by_extension ?? 0,
  coveredByEntitySource: coverage?.counts.covered_by_entity_source ?? 0,
  entityResponsibility: coverage?.counts.entity_responsibility ?? 0,
  automaticInsertion: automaticInsertionCount(coverage, version),
});

const indicatorViewModel = (
  indicator: ISecapRuntimeIndicator,
  coverageFields: readonly ISecapTemplateCoverageField[],
  version: ISecapVersion | null,
): ISecapIndicatorRowViewModel => {
  const templateConfig = version?.template_configuration.indicators[indicator.code];
  const configuredFieldUuids = templateConfig?.template_field_uuids ?? [];
  const coverageFieldsByUuid = new Map(
    coverageFields.map((field) => [field.field_uuid, field] as const),
  );
  const configuredCoverageFields = configuredFieldUuids
    .map((fieldUuid) => coverageFieldsByUuid.get(fieldUuid))
    .filter((field): field is ISecapTemplateCoverageField => field !== undefined);
  const mappingsKnown =
    configuredFieldUuids.length > 0 &&
    configuredCoverageFields.length === configuredFieldUuids.length;
  const canEditInsertionPolicy =
    mappingsKnown &&
    configuredCoverageFields.every((field) => field.coverage_owner !== 'user');
  const coverageStatus = coverageStatusForIndicator(coverageFields);

  return {
    code: indicator.code,
    name: indicator.name,
    description: indicator.description ?? '—',
    scope: indicator.scope,
    scopeLabel: scopeLabel(indicator.scope),
    sourceLabel: sourceLabel(indicator),
    runtimeState: indicator.runtime_state,
    runtimeStateLabel: runtimeStateLabel(indicator.runtime_state),
    executionReady: indicator.execution_ready,
    coverageStatus,
    coverageLabel: coverageLabel(coverageStatus),
    insertionPolicy: templateConfig?.insertion_policy ?? 'none',
    templateFieldCount: configuredFieldUuids.length,
    canEditInsertionPolicy,
  };
};

const buildSections = (
  catalog: ISecapRuntimeCatalog | null,
  coverage: ISecapTemplateCoverage | null,
  version: ISecapVersion | null,
): readonly ISecapIndicatorSectionViewModel[] => {
  const coverageByIndicator = fieldsByIndicatorCode(coverage, version);
  const sections = new Map<
    string,
    { indicators: ISecapIndicatorRowViewModel[]; coverageFields: ISecapTemplateCoverageField[] }
  >();

  for (const indicator of catalog?.indicators ?? []) {
    const sectionKey = indicator.secap_section || 'other';
    const section = sections.get(sectionKey) ?? { indicators: [], coverageFields: [] };
    const indicatorCoverage = coverageByIndicator.get(indicator.code) ?? [];
    section.indicators.push(indicatorViewModel(indicator, indicatorCoverage, version));
    section.coverageFields.push(...indicatorCoverage);
    sections.set(sectionKey, section);
  }

  return [...sections.entries()].map(([key, section]) => ({
    key,
    label: key,
    counts: countCoverageFields(section.coverageFields),
    indicators: section.indicators,
  }));
};

export const buildSecapFieldsViewModel = (
  catalog: ISecapRuntimeCatalog | null,
  coverage: ISecapTemplateCoverage | null,
  version: ISecapVersion | null,
): ISecapFieldsViewModel => ({
  summary: buildSummary(coverage, version),
  sections: buildSections(catalog, coverage, version),
  hasRuntimeCatalog: catalog !== null,
  hasTemplateCoverage: coverage !== null,
});

export const withIndicatorInsertionPolicy = (
  configuration: ISecapVersionTemplateConfiguration,
  indicatorCode: string,
  insertionPolicy: SecapTemplateInsertionPolicy,
): ISecapVersionTemplateConfiguration | null => {
  const current = configuration.indicators[indicatorCode];
  if (!current || current.template_field_uuids.length === 0) {
    return null;
  }
  if (current.insertion_policy === insertionPolicy) {
    return null;
  }

  return {
    indicators: {
      ...configuration.indicators,
      [indicatorCode]: {
        ...current,
        insertion_policy: insertionPolicy,
      },
    },
  };
};
